/**
 * Formulários de conta/feedback (Fase 4) — mutations POST autocontidas, com payloads
 * TIPADOS conforme o legado (não mais Record genérico):
 *  - useContact():  POST /app/contact/   { subject, message }                  [showContact 7280]
 *  - useSurvey():   POST /app/survey/    { quality, complement, question?, reasons? }
 *  - useCustomer(): POST /app/customer   { industry, customer, segment?, reach?, place?, aware?, other? }
 *  - useReferralLater(): POST /app/ambassador/later  "Agora não" no convite do programa
 *    (o backend manda, uma vez, o email que deixa o convite guardado)
 * Todas respondem { success } (ou vazio).
 */
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/apiClient';

async function postForm<T extends object>(path: string, body: T): Promise<{ success?: boolean }> {
  const result = await api.post<{ success?: boolean }>(path, body, { allowEmpty: true });
  if (!result.ok) throw new Error(result.code);
  return result.data ?? {};
}

export interface ContactPayload {
  /** Id de CONTACT_SUBJECTS (ContactDialog). */
  subject?: string;
  message: string;
}

export interface SurveyPayload {
  /** Nota de 0 a 10. */
  quality: number;
  /** Comentário aberto (opcional na avaliação nova; até 1000 caracteres). */
  complement: string;
  /** "goals": "quanto a ferramenta ajuda nas suas metas" (avaliação de 17/10/2026). */
  question?: 'goals';
  /** Motivos marcados (chips da faixa da nota), em português. */
  reasons?: string[];
}

export interface CustomerPayload {
  /** Setor, na chave antiga (`health_and_well_being`…). */
  industry?: string;
  /** Tipo de cliente, na chave antiga (`companies`…). */
  customer?: string;
  /** Segmento: nome da lista ou o texto do "Outro, qual?". */
  segment?: string;
  /** Alcance: cidade, regiao, estado, pais ou online. */
  reach?: string;
  /** Lugar que o painel achou pelo IP (só para o perfil; a pessoa troca na busca). */
  place?: { city?: string; region?: string; country?: string };
  /** "Como conheceu" — só na primeira vez. */
  aware?: string;
  /** Detalhe livre do "como conheceu" (quem indicou, qual evento…). */
  other?: string;
}

export function useContact() {
  return useMutation({ mutationFn: (body: ContactPayload) => postForm('app/contact/', body) });
}

export function useSurvey() {
  return useMutation({ mutationFn: (body: SurveyPayload) => postForm('app/survey/', body) });
}

export function useCustomer() {
  return useMutation({ mutationFn: (body: CustomerPayload) => postForm('app/customer', body) });
}

export function useReferralLater() {
  return useMutation({ mutationFn: () => postForm('app/ambassador/later', {}) });
}
